import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, Home, Trophy, LogOut, Menu, X, ChevronRight, ChevronLeft, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { userService } from '../services/userService';

const AppBar = ({
    showUpgradeButton = true,
    showPricingModal,
    setShowPricingModal,
    userData = null,
    onHistoryItemClick,
    isSidebarOpen,
    setIsSidebarOpen,
}) => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [networkHistory, setNetworkHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    const fetchInitialHistory = async () => {
        if (!currentUser) return;
        setIsLoadingHistory(true);
        try {
            const response = await userService.getSearchHistory(currentUser.uid, 10, null);
            console.log("Full history response from service:", response);
            setNetworkHistory(response.data || []);
            setNextCursor(response.pagination.next_cursor);
            setHasMore(response.pagination.has_more);
        } catch (error) {
            console.error('Error fetching initial network history:', error);
            setNetworkHistory([]);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleLoadMore = async () => {
        if (!currentUser || !hasMore || isLoadingHistory) return;
        setIsLoadingHistory(true);
        try {
            const response = await userService.getSearchHistory(currentUser.uid, 10, nextCursor);
            setNetworkHistory(prev => [...prev, ...(response.data || [])]);
            setNextCursor(response.pagination.next_cursor);
            setHasMore(response.pagination.has_more);
        } catch (error) {
            console.error('Error fetching more network history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    }

    // Effect to fetch when sidebar opens
    useEffect(() => {
        if (isSidebarOpen && currentUser) {
            fetchInitialHistory();
        }
    }, [isSidebarOpen, currentUser]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/auth');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleUpgradeClick = () => {
        console.log('Upgrade button clicked');
        if (setShowPricingModal) {
            setShowPricingModal(true);
        }
    };

    const isReferralPage = location.pathname === '/referral';
    const isLeaderboardPage = location.pathname === '/leaderboard';
    const isHomePage = location.pathname === '/';

    return (
        <>
            <motion.div
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
                className="w-full bg-white/60 backdrop-blur-md border-b border-gray-200/50 shadow-sm"
            >
                <div className="max-w-5xl mx-auto px-0 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {/* Network History Button */}
                            {currentUser && (
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                    className={`text-sm flex items-center space-x-1 px-2 py-2 rounded-lg transition-all duration-300 overflow-hidden group ${isSidebarOpen
                                            ? 'text-black font-medium bg-black/5'
                                            : 'text-gray-500 hover:text-black hover:bg-black/5'
                                        }`}
                                >
                                    <Menu className="w-5 h-5 flex-shrink-0" />
                                    <span className="whitespace-nowrap transition-all duration-300 ease-out transform -translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100">
                                        History
                                    </span>
                                </motion.button>
                            )}

                            <motion.a
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xl font-medium text-gray-900 hover:text-gray-700 transition-colors flex items-center"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <motion.div
                                    whileHover="fiddling"
                                    animate="idle"
                                    className="flex items-center"
                                >
                                    <motion.img
                                        src="/logo.png"
                                        alt="Quis Lab Logo"
                                        className="h-12 w-12 mr-2 animate-[wiggle_1.5s_ease-in-out_infinite]"
                                    />
                                    <span className="text-2xl font-extrabold tracking-tight">Quis</span>
                                </motion.div>
                            </motion.a>
                        </div>

                        <nav className="flex items-center space-x-6">
                            {!currentUser ? (
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => navigate('/auth')}
                                    className="text-sm font-medium text-gray-500 hover:text-black"
                                >
                                    Sign in
                                </motion.button>
                            ) : (
                                <>
                                    {/* Home Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => navigate('/')}
                                        className={`text-sm flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isHomePage
                                                ? 'text-black font-medium bg-black/5'
                                                : 'text-gray-500 hover:text-black hover:bg-black/5'
                                            }`}
                                    >
                                        <Home className="w-4 h-4" />
                                        <span>Home</span>
                                    </motion.button>

                                    {/* Profile Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => navigate('/referral')}
                                        className={`text-sm flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isReferralPage
                                                ? 'text-black font-medium bg-black/5'
                                                : 'text-gray-500 hover:text-black hover:bg-black/5'
                                            }`}
                                    >
                                        <Users className="w-4 h-4" />
                                        <span>Profile</span>
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => navigate('/leaderboard')}
                                        className={`text-sm flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isLeaderboardPage
                                                ? 'text-black font-medium bg-black/5'
                                                : 'text-gray-500 hover:text-black hover:bg-black/5'
                                            }`}
                                    >
                                        <Trophy className="w-4 h-4" />
                                        <span>Leaderboard</span>
                                    </motion.button>

                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleLogout}
                                        className="text-sm text-gray-500 hover:text-black flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-black/5 transition-colors"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        <span>Logout</span>
                                    </motion.button>

                                    {showUpgradeButton && (
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={handleUpgradeClick}
                                            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-black to-gray-800 rounded-lg hover:from-gray-800 hover:to-black transition-all duration-300 shadow-md"
                                        >
                                            Enhance Reach
                                        </motion.button>
                                    )}
                                </>
                            )}
                        </nav>
                    </div>
                </div>
            </motion.div>

            {/* Network History Sidebar */}
            <AnimatePresence>
                {isSidebarOpen && (
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 20 }}
                        className="fixed left-0 top-0 h-full w-80 bg-white/80 backdrop-blur-md shadow-xl border-r border-gray-200/50 z-40"
                    >
                        <div className="p-4 border-b border-gray-200/50">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900">My Network Searches</h2>
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setIsSidebarOpen(false)}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </motion.button>
                            </div>
                        </div>
                        <div className="overflow-y-auto h-[calc(100vh-64px)]">
                            <div className="p-4 space-y-4">
                                {isLoadingHistory && networkHistory.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full pt-16">
                                        <motion.img
                                            src="/logo.png"
                                            alt="Quis Lab Logo"
                                            className="h-16 w-16 mb-4"
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                        />
                                        <p className="text-sm text-gray-600">Loading History...</p>
                                    </div>
                                ) : networkHistory.length > 0 ? (
                                    <>
                                        {networkHistory.map((item, index) => (
                                            <motion.button
                                                key={item.id || index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                onClick={() => {
                                                    if (onHistoryItemClick) {
                                                        onHistoryItemClick(item.guid);
                                                    }
                                                }}
                                                className="w-full text-left bg-white/60 backdrop-blur-md border border-gray-200/50 rounded-2xl p-4 hover:shadow-lg hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-black transition-all duration-200"
                                            >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-2">
                                                <div className="h-8 w-8 bg-gradient-to-br from-gray-100 to-gray-50 rounded-full flex items-center justify-center text-gray-900 font-medium shadow-sm">
                                                    <Search className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 line-clamp-2">{item.search_query}</p>
                                                    <div className="flex items-center space-x-2 mt-0.5">
                                                        <p className="text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString()}</p>
                                                        <span className="text-xs text-gray-400">•</span>
                                                        <div className="bg-black/5 text-gray-700 text-xs font-medium px-2 py-0.5 rounded-full">
                                                            {item.matches_count || 0} matches
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.button>
                                        ))}
                                        {hasMore && (
                                            <motion.div className="text-center mt-4">
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={handleLoadMore}
                                                    disabled={isLoadingHistory}
                                                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-md disabled:bg-gray-400"
                                                >
                                                    {isLoadingHistory ? 'Loading...' : 'Load More'}
                                                </motion.button>
                                            </motion.div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-10">
                                        <p className="text-sm text-gray-500">No search history found.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AppBar; 
